import { useEffect, useState } from "react";
import {
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  FormSection,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import {
  AddOutlinedIcon,
  ArrowDownwardOutlinedIcon,
  ArrowUpwardOutlinedIcon,
  DeleteOutlinedIcon,
} from "../../ui/appIcons";
import { QuietButton } from "../../ui/QuietButton";
import {
  emptyField,
  emptySection,
  FIELD_TYPE_OPTIONS,
  type FormBuilderSavePayload,
  type FormFieldDef,
  type FormFieldType,
  type FormSectionDef,
  parseNotifyEmails,
  readFormSchema,
  SOURCE_TYPE_OPTIONS,
} from "./formSchema";

export type FormBuilderValue = {
  id?: string;
  name?: string | null;
  description?: string | null;
  schemaJson?: Record<string, unknown> | null;
  notifyEmails?: string[] | null;
  isAvailable?: boolean;
};

export type FormBuilderDialogProps = {
  open: boolean;
  value: FormBuilderValue | null;
  onClose: () => void;
  onSave: (payload: FormBuilderSavePayload) => void | Promise<void>;
  saving?: boolean;
  error?: string | null;
};

/**
 * Form builder dialog: create/edit a dynamic form (ported from Turbo FormBuilderDialog).
 * Collects name, description, logo, source, availability, notify emails, and sections/fields.
 */
export function FormBuilderDialog({
  open,
  value,
  onClose,
  onSave,
  saving = false,
  error = null,
}: FormBuilderDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [sourceType, setSourceType] = useState("link");
  const [notifyEmails, setNotifyEmails] = useState("");
  const [isAvailable, setIsAvailable] = useState(true);
  const [sections, setSections] = useState<FormSectionDef[]>([emptySection()]);

  useEffect(() => {
    if (!open) return;
    setName(value?.name || "");
    setDescription(value?.description || "");
    const schema = readFormSchema(value?.schemaJson ?? undefined);
    setLogoUrl(schema.logoUrl || "");
    setSourceType(schema.sourceType || "link");
    setNotifyEmails((value?.notifyEmails ?? []).join(", "));
    setIsAvailable(value?.isAvailable !== false);
    setSections(
      schema.sections.length > 0 ? schema.sections : [emptySection()],
    );
  }, [open, value]);

  const updateSection = (sid: string, patch: Partial<FormSectionDef>) => {
    setSections((prev) =>
      prev.map((s) => (s.id === sid ? { ...s, ...patch } : s)),
    );
  };

  const removeSection = (sid: string) => {
    setSections((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== sid) : prev));
  };

  const addField = (sid: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sid ? { ...s, fields: [...s.fields, emptyField()] } : s,
      ),
    );
  };

  const updateField = (sid: string, fid: string, patch: Partial<FormFieldDef>) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sid) return s;
        return {
          ...s,
          fields: s.fields.map((f) => (f.id === fid ? { ...f, ...patch } : f)),
        };
      }),
    );
  };

  const removeField = (sid: string, fid: string) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sid) return s;
        return { ...s, fields: s.fields.filter((f) => f.id !== fid) };
      }),
    );
  };

  const moveField = (sid: string, fid: string, dir: -1 | 1) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sid) return s;
        const idx = s.fields.findIndex((f) => f.id === fid);
        const target = idx + dir;
        if (idx < 0 || target < 0 || target >= s.fields.length) return s;
        const next = [...s.fields];
        [next[idx], next[target]] = [next[target], next[idx]];
        return { ...s, fields: next };
      }),
    );
  };

  const handleSave = () => {
    if (!name.trim() || saving) return;
    const normalizedSections = sections.map((s, si) => ({
      id: s.id,
      title: s.title,
      order: si,
      fields: s.fields.map((f, fi) => ({ ...f, order: fi })),
    }));
    void onSave({
      name: name.trim(),
      description: description.trim() || null,
      notifyEmails: parseNotifyEmails(notifyEmails),
      isAvailable,
      schemaJson: {
        sections: normalizedSections,
        logoUrl: logoUrl.trim() || undefined,
        sourceType,
      },
    });
  };

  return (
    <FormDialog
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      size="md"
    >
      <div dir="rtl" className="form-builder">
        <FormCard
          title={value?.id ? "עריכת טופס" : "יצירת טופס חדש"}
          error={error}
          onSave={handleSave}
          onCancel={onClose}
          saveLabel={value?.id ? "שמור שינויים" : "צור טופס"}
          cancelLabel="ביטול"
          saving={saving}
          saveDisabled={!name.trim()}
        >
          <FormSection title="פרטי טופס">
            <FormGrid cols={1}>
              <FormField
                label="שם הטופס"
                value={name}
                onChange={setName}
                required
                disabled={saving}
              />
              <FormField
                label="תיאור קצר"
                value={description}
                onChange={setDescription}
                rows={2}
                disabled={saving}
              />
              <FormField
                label="כתובת לוגו (URL)"
                value={logoUrl}
                onChange={setLogoUrl}
                placeholder="https://..."
                disabled={saving}
              />
            </FormGrid>
            <FormGrid cols={2}>
              <FormField
                label="מקור"
                value={sourceType}
                onChange={setSourceType}
                options={SOURCE_TYPE_OPTIONS}
                disabled={saving}
              />
              <FormField
                checkbox
                label="טופס זמין לציבור"
                checked={isAvailable}
                onChange={
                  ((v: boolean) => setIsAvailable(v)) as unknown as (
                    value: string,
                  ) => void
                }
                disabled={saving}
              />
            </FormGrid>
            <FormField
              label="מיילים להתראות על הגשות"
              value={notifyEmails}
              onChange={setNotifyEmails}
              placeholder="manager@example.com, office@example.com"
              hint="מופרדים בפסיק. אם ריק — ללא התראות במייל."
              disabled={saving}
            />
          </FormSection>

          <div className="form-builder__divider" />

          <div className="form-builder__sections-header">
            <h3 className="form-builder__sections-title">מקטעים ושדות</h3>
            <QuietButton
              tone="accent"
              icon={<AddOutlinedIcon />}
              title="הוסף מקטע"
              disabled={saving}
              onClick={() => setSections((prev) => [...prev, emptySection()])}
            >
              הוסף מקטע
            </QuietButton>
          </div>

          {sections.map((sec) => (
            <div key={sec.id} className="form-builder__section">
              <div className="form-builder__section-head">
                <div className="form-builder__section-title-field">
                  <FormField
                    label="כותרת מקטע"
                    value={sec.title}
                    onChange={(v: string) => updateSection(sec.id, { title: v })}
                    disabled={saving}
                  />
                </div>
                <QuietButton
                  tone="danger"
                  icon={<DeleteOutlinedIcon />}
                  title="מחק מקטע"
                  disabled={saving || sections.length === 1}
                  onClick={() => removeSection(sec.id)}
                />
              </div>

              <div className="form-builder__fields">
                {sec.fields.length === 0 ? (
                  <p className="form-builder__empty">אין שדות. לחץ &quot;הוסף שדה&quot;.</p>
                ) : null}
                {sec.fields.map((f) => (
                  <div key={f.id} className="form-builder__field">
                    <div className="form-builder__field-row">
                      <FormField
                        label="תווית"
                        value={f.label}
                        onChange={(v: string) =>
                          updateField(sec.id, f.id, { label: v })
                        }
                        disabled={saving}
                      />
                      <FormField
                        label="טקסט מקדים"
                        value={f.placeholder || ""}
                        onChange={(v: string) =>
                          updateField(sec.id, f.id, { placeholder: v })
                        }
                        disabled={saving}
                      />
                      <FormField
                        label="סוג"
                        value={f.type}
                        onChange={(v: string) =>
                          updateField(sec.id, f.id, {
                            type: v as FormFieldType,
                          })
                        }
                        options={FIELD_TYPE_OPTIONS}
                        disabled={saving}
                      />
                      <FormField
                        checkbox
                        label="חובה"
                        checked={!!f.required}
                        onChange={
                          ((v: boolean) =>
                            updateField(sec.id, f.id, {
                              required: v,
                            })) as unknown as (value: string) => void
                        }
                        disabled={saving}
                      />
                      <div className="form-builder__field-actions">
                        <QuietButton
                          icon={<ArrowUpwardOutlinedIcon />}
                          title="העבר למעלה"
                          disabled={saving}
                          onClick={() => moveField(sec.id, f.id, -1)}
                        />
                        <QuietButton
                          icon={<ArrowDownwardOutlinedIcon />}
                          title="העבר למטה"
                          disabled={saving}
                          onClick={() => moveField(sec.id, f.id, 1)}
                        />
                        <QuietButton
                          tone="danger"
                          icon={<DeleteOutlinedIcon />}
                          title="מחק שדה"
                          disabled={saving}
                          onClick={() => removeField(sec.id, f.id)}
                        />
                      </div>
                    </div>
                    {f.type === "dropdown" ? (
                        <FormField
                        label="אפשרויות בחירה (מופרדות בפסיק)"
                        value={(f.options || []).join(", ")}
                        onChange={(v: string) =>
                          updateField(sec.id, f.id, {
                            options: v
                              .split(",")
                              .map((s: string) => s.trim())
                              .filter(Boolean),
                          })
                        }
                        disabled={saving}
                      />
                    ) : null}
                  </div>
                ))}
                <QuietButton
                  tone="accent"
                  icon={<AddOutlinedIcon />}
                  title="הוסף שדה"
                  disabled={saving}
                  onClick={() => addField(sec.id)}
                >
                  הוסף שדה
                </QuietButton>
              </div>
            </div>
          ))}
        </FormCard>
      </div>
    </FormDialog>
  );
}
